/**
 * Require an active subscription (or org entitlement) only when `isWorkspaceBillingPayer(workspaceRole)` is true
 * (active workspace role is **OWNER**). Invited USER / CREATOR / ADMIN use the owner’s subscription for that
 * tenant; switching to their own default workspace or a workspace they create as OWNER triggers the payer rule.
 *
 * /pricing stays reachable from the nav for anyone who chooses to open it.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { isWorkspaceBillingPayer } from '@/utils/stepflowRoles';
import { isBillingUiEnabled } from '@/services/stripeBilling';

const BILLING_EXEMPT_PATHS = ['/pricing'];

export default function SubscriptionGate({ children }) {
  const { user, loading, billingLoading, hasActiveSubscription, workspaceRole, activeStripeGated } = useAuth();
  const { pathname } = useLocation();

  if (loading) {
    return null;
  }

  if (!user) {
    return null;
  }

  if (billingLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-sm">Switching workspace…</p>
        </div>
      </div>
    );
  }

  const exempt = BILLING_EXEMPT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  // Only a CONSUMER workspace goes through Stripe; an ENTERPRISE one is contract-billed and never gated
  // (LOGIN-TO-TENANT.md §1). Unknown (older server) → the payer rule as before.
  const mustPayForActiveWorkspace = isWorkspaceBillingPayer(workspaceRole) && activeStripeGated !== false;

  // Billing UI master switch off (single-account/no-Stripe): there is no
  // paywall and no /pricing page to redirect to — never gate. Prevents a
  // redirect loop with the PricingPage "→ home" guard.
  if (!isBillingUiEnabled()) {
    return children;
  }

  if (!hasActiveSubscription && !exempt && mustPayForActiveWorkspace) {
    return <Navigate to="/pricing?billing=required" replace />;
  }

  return children;
}
